import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getRecommendedServices, getPricing, approveProposal, requestRevision } from '../services/solution';
import { getAnalysisDocuments } from '../services/proposal';

export const ClientPortal = () => {
  const { id } = useParams();
  const [documents, setDocuments] = useState(null);
  const [services, setServices] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('proposal');
  const [feedback, setFeedback] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const [docs, recs, price] = await Promise.all([
          getAnalysisDocuments(id),
          getRecommendedServices(id),
          getPricing(id)
        ]);
        setDocuments(docs);
        setServices(recs);
        setPricing(price);
      } catch (error) {
        console.error("Failed to fetch client portal data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleApprove = async () => {
    if (!id || !documents?.proposal?.id) return;
    try {
      await approveProposal(documents.proposal.id);
      alert('Proposal Approved! A new Client Project has been created.');
    } catch (e) {
      alert('Error approving proposal.');
    }
  };

  const handleRequestRevision = async () => {
    if (!id || !documents?.proposal?.id) return;
    try {
      await requestRevision(documents.proposal.id, feedback);
      alert('Revision requested successfully.');
      setShowFeedbackModal(false);
    } catch (e) {
      alert('Error requesting revision.');
    }
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-pulse">Loading Client Portal...</div></div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8 border-b pb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Client Portal</h1>
          <p className="text-gray-500 mt-2">Review your tailored business proposal and recommended solutions.</p>
        </div>
        <div className="space-x-4 flex">
          <button onClick={() => setShowFeedbackModal(true)} className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg">
            Request Revision
          </button>
          <button onClick={handleApprove} className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg">
            Approve & Start Project
          </button>
        </div>
      </div>

      <div className="flex overflow-x-auto space-x-2 border-b border-gray-200 pb-2 mb-6 custom-scrollbar">
        {[
          { id: 'proposal', label: 'Proposal & Executive Summary' },
          { id: 'services', label: 'Recommended Services' },
          { id: 'pricing', label: 'Pricing & Timeline' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`whitespace-nowrap px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'proposal' && (
        <div className="space-y-8">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold mb-4">Executive Summary</h2>
            <div className="prose max-w-none bg-gray-50 p-6 rounded-lg border">
              {documents?.executive_report?.content ? (
                <pre className="whitespace-pre-wrap font-sans text-gray-800">{documents.executive_report.content}</pre>
              ) : (
                <p className="italic text-gray-500">No executive summary available.</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold mb-4">Full Proposal</h2>
            <div className="prose max-w-none bg-gray-50 p-6 rounded-lg border">
              {documents?.proposal?.content ? (
                <pre className="whitespace-pre-wrap font-sans text-gray-800">{documents.proposal.content}</pre>
              ) : (
                <p className="italic text-gray-500">No proposal available.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'services' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-xl font-bold text-gray-900">{service.name}</h3>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">{service.category}</span>
              </div>
              <p className="text-gray-600 mb-4">{service.description}</p>

              <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm text-gray-700">
                <div className="flex justify-between"><span className="font-semibold">Duration:</span> <span>{service.estimated_duration}</span></div>
                <div className="flex justify-between"><span className="font-semibold">Complexity:</span> <span>{service.complexity}</span></div>
                <div className="flex justify-between"><span className="font-semibold">Expected ROI:</span> <span>{service.expected_roi}</span></div>
              </div>

              <div className="mt-4">
                <h4 className="font-semibold mb-2">Deliverables:</h4>
                <ul className="list-disc pl-5 text-sm text-gray-600">
                  {service.deliverables.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'pricing' && pricing && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm border p-6 text-center">
              <span className="block text-gray-500 font-semibold mb-1">Estimated Budget</span>
              <span className="text-3xl font-bold text-gray-900">${pricing.min_budget.toLocaleString()} - ${pricing.max_budget.toLocaleString()}</span>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-6 text-center">
              <span className="block text-gray-500 font-semibold mb-1">Total Timeline</span>
              <span className="text-3xl font-bold text-gray-900">{pricing.timeline_weeks} Weeks</span>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-6 text-center">
              <span className="block text-gray-500 font-semibold mb-1">Total Effort</span>
              <span className="text-3xl font-bold text-gray-900">{pricing.total_hours} Hours</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6 mt-8">
            <h2 className="text-xl font-bold mb-6">Implementation Phases</h2>
            <div className="space-y-4">
              {pricing.phases.map((phase, idx) => (
                <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border">
                  <div>
                    <h3 className="font-semibold text-lg text-gray-800">{phase.name}</h3>
                    <p className="text-gray-600 text-sm mt-1">{phase.description}</p>
                  </div>
                  <div className="text-right">
                    <span className="block font-bold text-gray-900">${phase.cost.toLocaleString()}</span>
                    <span className="text-sm text-gray-500">{phase.hours} hrs</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Request Revision</h2>
            <p className="text-gray-600 mb-4">Please provide detailed feedback on what you would like changed in the proposal or pricing.</p>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="w-full h-32 border border-gray-300 rounded-lg p-3 mb-4 focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="e.g., The budget is slightly too high, can we reduce Phase 2?"
            />
            <div className="flex justify-end space-x-4">
              <button onClick={() => setShowFeedbackModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleRequestRevision} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Submit Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortal;
